import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { FieldWithId, GroupingWithId } from './MetadataInterfaces';
import { CustomControlType } from '../../../constants/Control';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { CollapsibleGroup } from './CollapsibleGroup';
import { MapEditor } from './MapEditor';

interface FieldFormProps {
  field?: FieldWithId;
  onSave: (field: Omit<FieldWithId, 'id'>) => void;
  onCancel: () => void;
  groupings: GroupingWithId[];
  availableControls: CustomControlType[];
  availableKeys: string[];
}

const FieldForm: React.FC<FieldFormProps> = ({
  field,
  onSave,
  onCancel,
  groupings,
  availableControls,
  availableKeys,
}) => {
  const { getFlags } = useFeatureFlags();
  const [key, setKey] = useState(field?.Key || '');
  const [sourceKey, setSourceKey] = useState(field?.SourceKey || undefined);
  const [label, setLabel] = useState(field?.Label || undefined);
  const [layout, setLayout] = useState(field?.Layout || 'fluid');
  const [flag, setFlag] = useState(field?.FeatureFlag || undefined);
  const [control, setControl] = useState(field?.Control || undefined);
  const [groupingKey, setGroupingKey] = useState(
    field?.GroupingKey || undefined,
  );
  const [forcedRequired, setForcedRequired] = useState(
    field?.ForceRequired || false,
  );
  const [forcedReadOnly, setForcedReadOnly] = useState(
    field?.ForcedReadOnly || false,
  );
  const [forcedNeverDisabled, setForcedNeverDisabled] = useState(
    field?.ForceNeverDisabled || false,
  );
  const [allowSetFieldDefaults, setAllowSetFieldDefaults] = useState(
    field?.AllowSetFieldDefaults || false,
  );
  const [hideFromMainForm, setHideFromMainForm] = useState(
    field?.HideFromMainForm || false,
  );
  const [traits, setTraits] = useState<Record<string, string>>(
    field?.Traits || {},
  );
  const [overrideRulesExpanded, setOverrideRulesExpanded] = useState(false);
  const [traitsExpanded, setTraitsExpanded] = useState(false);

  // Get all available flag keys from the getFlags object
  const availableFlagKeys = Object.keys(getFlags() || {});

  useEffect(() => {
    if (field) {
      setKey(field.Key);
      setSourceKey(field.SourceKey || undefined);
      setLabel(field.Label || undefined);
      setLayout(field.Layout || 'fluid');
      setControl(field.Control || undefined);
      setFlag(field.FeatureFlag || undefined);
      setGroupingKey(field.GroupingKey || undefined);
      setForcedRequired(field.ForceRequired || false);
      setForcedReadOnly(field.ForcedReadOnly || false);
      setForcedNeverDisabled(field.ForceNeverDisabled || false);
      setAllowSetFieldDefaults(field.AllowSetFieldDefaults || false);
      setHideFromMainForm(field.HideFromMainForm || false);
      setTraits(field.Traits || {});

      // Expand sections if they have values
      const hasOverrideRules = !!(field.ForceRequired || field.ForcedReadOnly || field.ForceNeverDisabled);
      setOverrideRulesExpanded(hasOverrideRules);

      const hasTraits = !!(field.Traits && Object.keys(field.Traits).length > 0);
      setTraitsExpanded(hasTraits);
    }
  }, [field]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      Key: key,
      SourceKey: sourceKey,
      Label: label,
      Layout: layout as 'fluid' | 'fluid-single' | 'paired' | 'single',
      Control: control,
      ForceRequired: forcedRequired,
      ForceNeverDisabled: forcedNeverDisabled,
      ForcedReadOnly: forcedReadOnly,
      GroupingKey: groupingKey,
      FeatureFlag: flag,
      AllowSetFieldDefaults: allowSetFieldDefaults,
      HideFromMainForm: hideFromMainForm,
      Traits: Object.keys(traits).length > 0 ? traits : undefined,
    });
  };

  // Validate that the flag is one of the available keys if provided
  const isValidFlag = !flag || availableFlagKeys.includes(flag);

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Autocomplete
        freeSolo
        options={availableKeys}
        value={key}
        onChange={(_, newValue) => setKey(newValue || '')}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Key"
            fullWidth
            margin="normal"
            onChange={(e) => setKey(e.target.value)}
          />
        )}
      />
      <TextField
        fullWidth
        label="Label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        margin="normal"
      />
      <FormControl fullWidth margin="normal">
        <InputLabel>Layout</InputLabel>
        <Select
          value={layout}
          onChange={(e) =>
            setLayout(
              e.target.value as 'fluid' | 'fluid-single' | 'paired' | 'single',
            )
          }
        >
          <MenuItem value="single">Single</MenuItem>
          <MenuItem value="fluid">Fluid</MenuItem>
          <MenuItem value="fluid-single">Fluid Single</MenuItem>
          <MenuItem value="paired">Paired</MenuItem>
        </Select>
      </FormControl>
      <Autocomplete
        options={availableControls}
        getOptionLabel={(option) => option.name}
        value={availableControls.find((c) => c.key === control) || null}
        onChange={(_, newValue) => setControl(newValue ? newValue.key : '')}
        renderInput={(params) => (
          <TextField {...params} label="Control" fullWidth margin="normal" />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography variant="body1">{option.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {option.description}
              </Typography>
            </Box>
          </li>
        )}
      />
      <Autocomplete
        options={groupings}
        getOptionLabel={(option) => `${option.Key} - ${option.Title}`}
        value={groupings.find((g) => g.Key === groupingKey) || null}
        onChange={(_, newValue) => setGroupingKey(newValue ? newValue.Key : '')}
        renderInput={(params) => (
          <TextField {...params} label="Grouping" fullWidth margin="normal" />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography variant="body1">{option.Title}</Typography>
              <Typography variant="body2" color="text.secondary">
                Key: {option.Key}
              </Typography>
            </Box>
          </li>
        )}
      />
      <Autocomplete
        freeSolo
        options={availableKeys}
        value={sourceKey}
        onChange={(_, newValue) => setSourceKey(newValue || '')}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Source Key"
            fullWidth
            margin="normal"
            onChange={(e) => setSourceKey(e.target.value)}
          />
        )}
      />
      {/* Flag Autocomplete */}
      <Autocomplete
        options={availableFlagKeys}
        value={flag || null}
        onChange={(_, newValue) => setFlag(newValue || undefined)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Feature Flag"
            fullWidth
            margin="normal"
            error={!isValidFlag}
            helperText={
              !isValidFlag ? 'Flag must match an existing feature flag key' : ''
            }
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <Box>
              <Typography variant="body1">{option}</Typography>
            </Box>
          </li>
        )}
      />
      <CollapsibleGroup
        label="Override Rules"
        isExpanded={overrideRulesExpanded}
        hasValues={forcedRequired || forcedReadOnly || forcedNeverDisabled}
        onToggle={setOverrideRulesExpanded}
        onClear={() => {
          setForcedRequired(false);
          setForcedReadOnly(false);
          setForcedNeverDisabled(false);
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={forcedRequired}
              onChange={(e) => setForcedRequired(e.target.checked)}
            />
          }
          label="Force Required"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={forcedReadOnly}
              onChange={(e) => setForcedReadOnly(e.target.checked)}
            />
          }
          label="Force Read Only"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={forcedNeverDisabled}
              onChange={(e) => setForcedNeverDisabled(e.target.checked)}
            />
          }
          label="Force Never Disabled"
        />
      </CollapsibleGroup>
      <Box
        sx={{
          backgroundColor: 'background.secondary',
          paddingX: 2,
          paddingY: 1,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={allowSetFieldDefaults}
              onChange={(e) => setAllowSetFieldDefaults(e.target.checked)}
            />
          }
          label="Allow Set Field Defaults"
        />
        {allowSetFieldDefaults && (
          <FormControlLabel
            sx={{
              width: '100%',
              backgroundColor: 'warning.light',
              border: '1px dashed',
              borderColor: 'warning.main',
              marginX: 0,
            }}
            control={
              <Checkbox
                checked={hideFromMainForm}
                onChange={(e) => setHideFromMainForm(e.target.checked)}
              />
            }
            label="Hide From Main Form"
          />
        )}
      </Box>
      <CollapsibleGroup
        label="Enable Traits"
        isExpanded={traitsExpanded}
        hasValues={Object.keys(traits).length > 0}
        onToggle={setTraitsExpanded}
        onClear={() => setTraits({})}
      >
        <MapEditor value={traits} onChange={setTraits} />
      </CollapsibleGroup>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary">
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default FieldForm;
